import React, { useEffect, useState } from 'react';
import {
  Box, Heading, Text, VStack, SimpleGrid, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
} from '@chakra-ui/react';
import Page from '../../components/ui/Page';
import SectionCard from '../../components/ui/SectionCard';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import LoadingState from '../../components/ui/LoadingState';
import ErrorState from '../../components/ui/ErrorState';
import client from '../../utils/fetchClient';
import { useProjectContext } from '../../context/ProjectContext';

const tile = (label, value, sub, color = 'gray.900') => (
  <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px" p={4}>
    <Text fontSize="xs" color="gray.500" mb={1.5}>{label}</Text>
    <Text fontSize="24px" fontWeight="800" color={color}>{value}</Text>
    {sub && <Text fontSize="xs" color="gray.400">{sub}</Text>}
  </Box>
);

export default function TestingGapsPage() {
  const { effectiveProjectParam } = useProjectContext();
  const [state, setState] = useState({ loading: true, error: null, modules: [], summary: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const q = effectiveProjectParam ? { project: effectiveProjectParam } : {};
        const [mod, met] = await Promise.all([
          client.get('/api/modules', { query: { ...q, page_size: 100 } }),
          client.get('/api/metrics', { query: q }),
        ]);
        const modules = mod.ok ? (await mod.json()).data || [] : [];
        const summary = met.ok ? (await met.json()).summary : null;
        if (!cancelled) setState({ loading: false, error: null, modules, summary });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e.message || 'Failed to load', modules: [], summary: null });
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveProjectParam]);

  const { loading, error, modules } = state;

  // "Gaps" heuristic: thin coverage (few tests) or unstable (flaky / low pass rate).
  const thin = [...modules].filter((m) => (m.testCount || 0) <= 3).sort((a, b) => (a.testCount || 0) - (b.testCount || 0)).slice(0, 8);
  const unstable = [...modules]
    .map((m) => ({ ...m, flakyPct: m.testCount ? ((m.flakyCount || 0) / m.testCount) * 100 : 0 }))
    .filter((m) => m.flakyPct > 0 || (m.passRate || 100) < 90)
    .sort((a, b) => (b.flakyPct + (100 - (b.passRate || 100))) - (a.flakyPct + (100 - (a.passRate || 100))))
    .slice(0, 8);

  return (
    <Page maxW="6xl">
      <Breadcrumbs items={[{ label: 'AI Analysis', to: '/analysis/release-readiness' }, { label: 'Testing gaps' }]} />
      <Heading size="lg" fontWeight="800" letterSpacing="-0.5px" color="gray.900" mb={1}>Testing gaps</Heading>
      <Text fontSize="sm" color="gray.500" mb={5}>Suites that are thinly covered or too unstable to trust.</Text>

      {loading && <LoadingState message="Finding gaps" />}
      {error && <ErrorState message={error} onRetry={() => window.location.reload()} />}

      {!loading && !error && (
        <VStack align="stretch" spacing={5}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3.5}>
            {tile('Suites tracked', modules.length)}
            {tile('Thinly covered', thin.length, '≤ 3 tests', thin.length ? 'status.flakyText' : 'gray.900')}
            {tile('Unstable suites', unstable.length, 'flaky or < 90% pass', unstable.length ? 'status.fail' : 'gray.900')}
            {tile('Total tests', modules.reduce((n, m) => n + (m.testCount || 0), 0))}
          </SimpleGrid>

          <SectionCard>
            <Heading size="sm" color="gray.900" mb={3}>Thinly covered suites</Heading>
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead><Tr><Th>Suite</Th><Th isNumeric>Tests</Th><Th isNumeric>Pass rate</Th></Tr></Thead>
                <Tbody>
                  {thin.map((m) => (
                    <Tr key={m.module}>
                      <Td fontWeight="600" color="gray.900">{m.module}</Td>
                      <Td isNumeric color="status.flakyText" fontWeight="700">{m.testCount}</Td>
                      <Td isNumeric color="gray.600">{Math.round(m.passRate || 0)}%</Td>
                    </Tr>
                  ))}
                  {thin.length === 0 && <Tr><Td colSpan={3}><Text fontSize="sm" color="gray.400">Every suite has meaningful coverage.</Text></Td></Tr>}
                </Tbody>
              </Table>
            </TableContainer>
          </SectionCard>

          <SectionCard>
            <Heading size="sm" color="gray.900" mb={3}>Unstable suites</Heading>
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead><Tr><Th>Suite</Th><Th isNumeric>Flaky</Th><Th isNumeric>Pass rate</Th><Th isNumeric>Tests</Th></Tr></Thead>
                <Tbody>
                  {unstable.map((m) => (
                    <Tr key={m.module}>
                      <Td fontWeight="600" color="gray.900">{m.module}</Td>
                      <Td isNumeric color="status.flaky" fontWeight="700">{m.flakyCount || 0}</Td>
                      <Td isNumeric color={(m.passRate || 100) < 90 ? 'status.fail' : 'gray.600'}>{Math.round(m.passRate || 0)}%</Td>
                      <Td isNumeric color="gray.500">{m.testCount}</Td>
                    </Tr>
                  ))}
                  {unstable.length === 0 && <Tr><Td colSpan={4}><Text fontSize="sm" color="gray.400">No unstable suites.</Text></Td></Tr>}
                </Tbody>
              </Table>
            </TableContainer>
          </SectionCard>
        </VStack>
      )}
    </Page>
  );
}
